/**
 * Magic Zoom Plus - Gallery Integration
 * Syncs MagicZoom overlay with Swiper gallery slides.
 * On mobile, the expand icon triggers the lightbox.
 * Falls back to product modal when MagicZoom is not available.
 */
(() => {
  'use strict';

  /**
   * Fixes accessibility issue where Magic Zoom Plus creates images without alt attributes.
   * Observes the DOM for dynamically created zoom window images and adds empty alt text.
   */
  const fixMagicZoomAccessibility = () => {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType !== Node.ELEMENT_NODE) return;

          /* Fix images inside Magic Zoom containers */
          const images = node.matches('img') ? [node] : node.querySelectorAll?.('img') || [];
          images.forEach((img) => {
            if (!img.hasAttribute('alt') && img.closest('.mz-zoom-window, .magic-hidden-wrapper')) {
              img.setAttribute('alt', '');
            }
          });
        });
      });
    });

    observer.observe(document.body, { childList: true, subtree: true });

    /* Fix any existing images on load */
    document.querySelectorAll('.mz-zoom-window img, .magic-hidden-wrapper img').forEach((img) => {
      if (!img.hasAttribute('alt')) {
        img.setAttribute('alt', '');
      }
    });
  };

  const MOBILE_BREAKPOINT = 750;
  const pendingSwitch = new Map();
  const intendedSlide = new Map();
  let initialized = false;

  /**
   * Checks if Magic Zoom Plus is loaded and ready
   * @returns {boolean}
   */
  const isMagicZoomReady = () => typeof MagicZoom !== 'undefined';

  /**
   * Checks if we're on mobile
   * @returns {boolean}
   */
  const isMobile = () => window.innerWidth < MOBILE_BREAKPOINT;

  /**
   * Gets the MagicZoom overlay ID for a gallery
   * @param {HTMLElement} gallery - The product-media-gallery element
   * @returns {string|null}
   */
  const getOverlayId = (gallery) => {
    const sectionId = gallery.getAttribute('data-section-id');
    return sectionId ? `mz-gallery-overlay-${sectionId}` : null;
  };

  /**
   * Gets the active media ID from the gallery/swiper
   * @param {HTMLElement} gallery - The product-media-gallery element
   * @param {HTMLElement|null} swiperContainer - Swiper container element
   * @returns {string|null}
   */
  const getActiveMediaId = (gallery, swiperContainer) => {
    if (gallery?.getActiveMediaId) {
      return gallery.getActiveMediaId();
    }

    const activeSlide = swiperContainer?.querySelector('swiper-slide.swiper-slide-active');
    return activeSlide?.getAttribute('data-media-id') || null;
  };

  /**
   * Finds MagicZoom selector element by media ID
   * @param {HTMLElement} gallery - The product-media-gallery element
   * @param {string} overlayId - MagicZoom overlay ID
   * @param {string|null} mediaId - Media ID to match
   * @returns {HTMLElement|null}
   */
  const getSelectorByMediaId = (gallery, overlayId, mediaId) => {
    if (!gallery || !overlayId || !mediaId) return null;

    return gallery.querySelector(
      `.mz-selectors a[data-zoom-id="${overlayId}"][data-media-id="${mediaId}"]`
    );
  };

  /**
   * Finds MagicZoom selector index by media ID
   * @param {HTMLElement} gallery - The product-media-gallery element
   * @param {string} overlayId - MagicZoom overlay ID
   * @param {string|null} mediaId - Media ID to match
   * @returns {number}
   */
  const getSelectorIndexByMediaId = (gallery, overlayId, mediaId) => {
    if (!gallery || !overlayId || !mediaId) return -1;

    const selectors = gallery.querySelectorAll(
      `.mz-selectors a[data-zoom-id="${overlayId}"]`
    );

    for (let i = 0; i < selectors.length; i += 1) {
      if (selectors[i].getAttribute('data-media-id') === String(mediaId)) {
        return i;
      }
    }

    return -1;
  };

  /**
   * Updates MagicZoom overlay with image URLs from selector
   * Directly updates DOM attributes and calls MagicZoom.update() API
   * @param {string} overlayId - MagicZoom overlay ID
   * @param {HTMLElement} selector - Selector element with image URLs
   * @returns {void}
   */
  const updateOverlayFromSelector = (overlayId, selector) => {
    if (!selector) return;

    const largeUrl = selector.getAttribute('href');
    const smallUrl = selector.getAttribute('data-image');

    if (!largeUrl || !smallUrl) return;

    /* Directly update overlay element's DOM attributes */
    const overlayEl = document.getElementById(overlayId);
    if (overlayEl) {
      overlayEl.setAttribute('href', largeUrl);
      const overlayImg = overlayEl.querySelector('img');
      if (overlayImg) {
        overlayImg.setAttribute('src', smallUrl);
      }
    }

    /* Note: MagicZoom.update() can break expand, so we only update DOM */
  };

  /**
   * Opens the fallback modal
   * @param {string} modalSelector - The modal element selector
   * @returns {void}
   */
  const openFallbackModal = (modalSelector) => {
    const modal = document.querySelector(modalSelector);
    if (!modal) return;

    if (typeof modal.show === 'function') {
      modal.show();
      return;
    }

    if (modal.classList) {
      modal.classList.add('is-open');
      document.body.classList.add('overflow-hidden');
    }
  };

  /**
   * Extracts zoom ID from MagicZoom callback argument
   * @param {string|HTMLElement|Object} arg - Callback argument
   * @returns {string|null}
   */
  const getZoomIdFromArg = (arg) => {
    if (typeof arg === 'string') return arg;
    return arg?.id || (arg?.getAttribute ? arg.getAttribute('id') : null);
  };

  /**
   * Finds gallery element by overlay ID
   * @param {string} zoomId - MagicZoom overlay ID
   * @returns {HTMLElement|null}
   */
  const getGalleryByOverlayId = (zoomId) => {
    const sectionId = zoomId.replace('mz-gallery-overlay-', '');
    return document.querySelector(`product-media-gallery[data-section-id="${sectionId}"]`);
  };

  /**
   * Hooks MagicZoom callbacks for gallery sync
   * - onExpandOpen: switches to correct image after expand opens
   * - onExpandClose: syncs MZ to current gallery position when closing
   * @returns {void}
   */
  const ensureExpandHook = () => {
    /**
     * @param {Object} options - MagicZoom options object
     * @returns {void}
     */
    const hookOptions = (options) => {
      if (!options || options._galleryHooked) return;

      const previousOpen = options.onExpandOpen;
      /**
       * Handles expand open - switches to queued image if pending
       * @param {string|HTMLElement|Object} arg - Expand open argument
       * @returns {void}
       */
      options.onExpandOpen = function onExpandOpen(arg) {
        if (typeof previousOpen === 'function') {
          try {
            previousOpen.apply(this, arguments);
          } catch (e) {
          }
        }

        const zoomId = getZoomIdFromArg(arg);
        if (!zoomId) return;

        const index = pendingSwitch.get(zoomId);
        if (index === undefined) return;

        pendingSwitch.delete(zoomId);

        try {
          MagicZoom.switchTo(zoomId, index);
        } catch (e) {
        }
      };

      const previousClose = options.onExpandClose;
      /**
       * Handles expand close - syncs overlay DOM to current gallery position
       * Updates overlay image URLs so next expand opens with correct image.
       * Uses direct DOM update instead of MagicZoom.switchTo() to avoid
       * visual flash when lightbox closes.
       * @param {string|HTMLElement|Object} arg - Expand close argument
       * @returns {void}
       */
      options.onExpandClose = function onExpandClose(arg) {
        if (typeof previousClose === 'function') {
          try {
            previousClose.apply(this, arguments);
          } catch (e) {
          }
        }

        const zoomId = getZoomIdFromArg(arg);
        if (!zoomId) return;

        const gallery = getGalleryByOverlayId(zoomId);
        if (!gallery) return;

        const swiperContainer = gallery.querySelector('.product-media-main-swiper');
        const activeMediaId = getActiveMediaId(gallery, swiperContainer);
        const selector = getSelectorByMediaId(gallery, zoomId, activeMediaId);

        if (selector) {
          /*
           * Update overlay DOM directly instead of calling MagicZoom.switchTo().
           * This keeps the overlay in sync without triggering MagicZoom transitions
           * that would cause a visual flash when the lightbox closes.
           */
          updateOverlayFromSelector(zoomId, selector);
        }
      };

      options._galleryHooked = true;
    };

    hookOptions(window.mzOptions);
    hookOptions(window.mzMobileOptions);
  };

  /**
   * Initializes slide change listener for a gallery
   * Updates overlay DOM attributes to keep MagicZoom in sync without triggering
   * internal transitions. Uses direct DOM updates instead of MagicZoom.switchTo()
   * to prevent flickering during thumbnail navigation.
   * @param {HTMLElement} gallery - The product-media-gallery element
   * @returns {void}
   */
  const initSlideChangeListener = (gallery) => {
    const overlayId = getOverlayId(gallery);
    if (!overlayId) return;

    const swiperContainer = gallery.querySelector('.product-media-main-swiper');
    if (!swiperContainer) return;

    swiperContainer.addEventListener('swiperslidechange', () => {
      if (isMobile()) return;
      if (!isMagicZoomReady()) return;

      const activeMediaId = getActiveMediaId(gallery, swiperContainer);
      const selector = getSelectorByMediaId(gallery, overlayId, activeMediaId);

      if (selector) {
        /*
         * Update overlay DOM directly instead of calling MagicZoom.switchTo().
         * MagicZoom.switchTo() triggers internal transitions that cause flickering
         * when rapidly navigating thumbnails. Direct DOM updates keep the overlay
         * in sync without visual artifacts.
         */
        updateOverlayFromSelector(overlayId, selector);
      }
    });
  };

  /**
   * Initializes thumbnail click tracking
   * Tracks which thumbnail was clicked before swiper transition completes
   * @param {HTMLElement} gallery - The product-media-gallery element
   * @returns {void}
   */
  const initThumbnailTracker = (gallery) => {
    const sectionId = gallery.getAttribute('data-section-id');
    const thumbsContainer = gallery.querySelector('.product-media-thumbs-swiper');
    if (!thumbsContainer) return;

    thumbsContainer.addEventListener('click', (event) => {
      const thumbnail = event.target.closest('swiper-slide');
      if (!thumbnail) return;

      const mediaId = thumbnail.getAttribute('data-media-id');
      if (mediaId) {
        intendedSlide.set(sectionId, mediaId);
      }
    }, true);
  };

  /**
   * Initializes desktop overlay click handler
   * Intercepts clicks to ensure correct image is shown when expand opens
   * @param {HTMLElement} gallery - The product-media-gallery element
   * @returns {void}
   */
  const initDesktopOverlayHandler = (gallery) => {
    const overlayId = getOverlayId(gallery);
    if (!overlayId) return;

    const overlayWrap = gallery.querySelector('.mz-gallery-overlay-wrap');
    if (!overlayWrap) return;

    const sectionId = gallery.getAttribute('data-section-id');

    let isProcessingClick = false;

    /* Use capture phase to intercept before MagicZoom's handler */
    overlayWrap.addEventListener('click', (event) => {
      if (isMobile()) return;
      if (!isMagicZoomReady()) return;

      /* Don't intercept if clicking expand icon or Door Vision button */
      if (event.target.closest('.product-media-icon-enlarge')) return;
      if (event.target.closest('.product-media-door-vision')) return;

      /* Prevent duplicate processing from nested elements */
      if (isProcessingClick) {
        event.preventDefault();
        event.stopImmediatePropagation();
        return;
      }

      event.preventDefault();
      event.stopImmediatePropagation();

      isProcessingClick = true;

      /* Use tracked intended slide if available (user clicked thumbnail recently) */
      let activeMediaId = intendedSlide.get(sectionId);

      /* Fallback to swiper's current state if no tracked slide */
      if (!activeMediaId) {
        const swiperContainer = gallery.querySelector('.product-media-main-swiper');
        const swiper = swiperContainer?.swiper;
        if (swiper) {
          const activeSlide = swiperContainer.querySelectorAll('swiper-slide')[swiper.activeIndex];
          activeMediaId = activeSlide?.getAttribute('data-media-id');
        } else {
          activeMediaId = getActiveMediaId(gallery, swiperContainer);
        }
      }

      const selectorIndex = getSelectorIndexByMediaId(gallery, overlayId, activeMediaId);

      /* Clear tracked slide after use */
      intendedSlide.delete(sectionId);

      /* Find the target MZ selector to get correct image URLs */
      const selector = getSelectorByMediaId(gallery, overlayId, activeMediaId);

      /*
       * If no matching selector (e.g., video slide, or selectorIndex is -1),
       * let MagicZoom handle natively via overlay click.
       */
      if (selectorIndex < 0 || !selector) {
        isProcessingClick = false;
        const overlay = document.getElementById(overlayId);
        if (overlay) {
          overlay.click();
        }
        return;
      }

      /*
       * Queue the switch for onExpandOpen callback.
       * We always use pendingSwitch (even for index 0) because MagicZoom's
       * internal state may not match the gallery after lightbox navigation.
       */
      pendingSwitch.set(overlayId, selectorIndex);

      /* Open lightbox - onExpandOpen will switch to correct image */
      isProcessingClick = false;
      try {
        MagicZoom.expand(overlayId);
      } catch (e) {
        openFallbackModal(`#ProductModal-${sectionId}`);
      }
    }, true);
  };

  /**
   * Initializes mobile click handler for expand icon and image area
   * Pre-switches to correct image before opening expand to avoid transition effect
   * @param {HTMLElement} gallery - The product-media-gallery element
   * @returns {void}
   */
  const initMobileClickHandler = (gallery) => {
    const overlayId = getOverlayId(gallery);
    if (!overlayId) return;

    gallery.addEventListener('click', (event) => {
      if (!isMobile() || !isMagicZoomReady()) return;

      /* Check if click is on expand icon or image area */
      const expandIcon = event.target.closest('.product-media-icon-enlarge');
      const imageArea = event.target.closest('.product-modal-opener-image');

      if (!expandIcon && !imageArea) return;

      event.preventDefault();
      event.stopPropagation();

      const swiperContainer = gallery.querySelector('.product-media-main-swiper');
      const activeMediaId = getActiveMediaId(gallery, swiperContainer);
      const selectorIndex = getSelectorIndexByMediaId(gallery, overlayId, activeMediaId);

      /**
       * Opens expand view with correct image
       * @returns {void}
       */
      const openExpand = () => {
        try {
          MagicZoom.expand(overlayId);
        } catch (e) {
          const sectionId = gallery.getAttribute('data-section-id');
          openFallbackModal(`#ProductModal-${sectionId}`);
        }
      };

      /*
       * Queue switch for onExpandOpen callback.
       * We always queue (even for index 0) because MagicZoom's internal state
       * may not match the gallery after lightbox navigation.
       */
      if (selectorIndex >= 0) {
        pendingSwitch.set(overlayId, selectorIndex);
      }
      openExpand();
    });
  };

  /**
   * Handles click when MagicZoom is not available
   * @param {Event} event - Click event
   * @returns {void}
   */
  const handleFallbackClick = (event) => {
    const overlayWrap = event.target.closest('.mz-gallery-overlay-wrap');
    const expandIcon = event.target.closest('.product-media-icon-enlarge');
    const imageArea = isMobile() ? event.target.closest('.product-modal-opener-image') : null;

    if (!overlayWrap && !expandIcon && !imageArea) return;

    event.preventDefault();
    event.stopPropagation();

    const gallery = (overlayWrap || expandIcon || imageArea).closest('product-media-gallery');
    if (!gallery) return;

    const sectionId = gallery.getAttribute('data-section-id');
    openFallbackModal(`#ProductModal-${sectionId}`);
  };

  /**
   * Initializes the integration
   * @returns {void}
   */
  const init = () => {
    if (initialized) return;
    initialized = true;

    fixMagicZoomAccessibility();

    const galleries = document.querySelectorAll('product-media-gallery');

    if (isMagicZoomReady()) {
      ensureExpandHook();

      galleries.forEach((gallery) => {
        const overlayId = getOverlayId(gallery);
        if (!overlayId) return;

        initMobileClickHandler(gallery);
        initDesktopOverlayHandler(gallery);
        initThumbnailTracker(gallery);

        setTimeout(() => {
          if (!MagicZoom.running(overlayId)) return;
          initSlideChangeListener(gallery);
        }, 100);
      });
    } else {
      document.addEventListener('click', handleFallbackClick, true);
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
